// Pure data shared by the app and the build (page generation, sitemap).
// Do not import anything DOM-related here.

export const LANGS = ['ca', 'es', 'gl'] as const;
export type Lang = (typeof LANGS)[number];
export const DEFAULT_LANG: Lang = 'ca';

export const isLang = (v: unknown): v is Lang => typeof v === 'string' && (LANGS as readonly string[]).includes(v);

export type CategoryId = 'organize' | 'optimize' | 'convertTo' | 'convertFrom' | 'edit' | 'security';
export const CATEGORIES: CategoryId[] = ['organize', 'optimize', 'convertTo', 'convertFrom', 'edit', 'security'];

export type ToolId =
  | 'merge'
  | 'split'
  | 'extract'
  | 'delete'
  | 'organize'
  | 'rotate'
  | 'compress'
  | 'repair'
  | 'ocr'
  | 'imagesToPdf'
  | 'wordToPdf'
  | 'excelToPdf'
  | 'pptToPdf'
  | 'pdfToImages'
  | 'pdfToText'
  | 'pdfToWord'
  | 'pdfa'
  | 'pageNumbers'
  | 'watermark'
  | 'sign'
  | 'flatten'
  | 'metadata'
  | 'protect'
  | 'unlock'
  | 'redact';

/** Kinds of input a tool accepts. */
export type InputKind = 'pdf' | 'image' | 'word' | 'excel' | 'powerpoint';

export const INPUT_EXTENSIONS: Record<InputKind, string[]> = {
  pdf: ['pdf'],
  image: ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'],
  word: ['doc', 'docx', 'odt', 'rtf', 'txt'],
  excel: ['xls', 'xlsx', 'ods', 'csv'],
  powerpoint: ['ppt', 'pptx', 'odp'],
};

/** Optional, separately downloaded engines. */
export type Engine = 'ocr' | 'gs' | 'mupdf' | 'office';

export interface ToolMeta {
  id: ToolId;
  category: CategoryId;
  /** Lucide icon name (see src/ui/icons.ts). */
  icon: string;
  slugs: Record<Lang, string>;
  accept: InputKind[];
  multiple: boolean;
  /** Minimum number of files before the tool can run. */
  minFiles?: number;
  engine?: Engine;
  popular?: boolean;
}

export const TOOLS: ToolMeta[] = [
  // Organise
  { id: 'merge', category: 'organize', icon: 'combine', slugs: { ca: 'unir-pdf', es: 'unir-pdf', gl: 'unir-pdf' }, accept: ['pdf', 'image'], multiple: true, minFiles: 2, popular: true },
  { id: 'split', category: 'organize', icon: 'scissors', slugs: { ca: 'dividir-pdf', es: 'dividir-pdf', gl: 'dividir-pdf' }, accept: ['pdf'], multiple: false, popular: true },
  { id: 'extract', category: 'organize', icon: 'file-output', slugs: { ca: 'extreure-pagines', es: 'extraer-paginas', gl: 'extraer-paxinas' }, accept: ['pdf'], multiple: false },
  { id: 'delete', category: 'organize', icon: 'file-minus', slugs: { ca: 'eliminar-pagines', es: 'eliminar-paginas', gl: 'eliminar-paxinas' }, accept: ['pdf'], multiple: false, popular: true },
  { id: 'organize', category: 'organize', icon: 'layout-grid', slugs: { ca: 'organitzar-pdf', es: 'organizar-pdf', gl: 'organizar-pdf' }, accept: ['pdf', 'image'], multiple: true, popular: true },
  { id: 'rotate', category: 'organize', icon: 'rotate-cw', slugs: { ca: 'girar-pdf', es: 'rotar-pdf', gl: 'xirar-pdf' }, accept: ['pdf'], multiple: false },
  // Optimise
  { id: 'compress', category: 'optimize', icon: 'minimize-2', slugs: { ca: 'comprimir-pdf', es: 'comprimir-pdf', gl: 'comprimir-pdf' }, accept: ['pdf'], multiple: true, engine: 'gs', popular: true },
  { id: 'repair', category: 'optimize', icon: 'wrench', slugs: { ca: 'reparar-pdf', es: 'reparar-pdf', gl: 'reparar-pdf' }, accept: ['pdf'], multiple: true },
  { id: 'ocr', category: 'optimize', icon: 'scan-text', slugs: { ca: 'ocr-pdf', es: 'ocr-pdf', gl: 'ocr-pdf' }, accept: ['pdf', 'image'], multiple: false, engine: 'ocr', popular: true },
  // Convert to PDF
  { id: 'imagesToPdf', category: 'convertTo', icon: 'image', slugs: { ca: 'jpg-a-pdf', es: 'jpg-a-pdf', gl: 'jpg-a-pdf' }, accept: ['image'], multiple: true, popular: true },
  { id: 'wordToPdf', category: 'convertTo', icon: 'file-text', slugs: { ca: 'word-a-pdf', es: 'word-a-pdf', gl: 'word-a-pdf' }, accept: ['word'], multiple: true, engine: 'office', popular: true },
  { id: 'excelToPdf', category: 'convertTo', icon: 'sheet', slugs: { ca: 'excel-a-pdf', es: 'excel-a-pdf', gl: 'excel-a-pdf' }, accept: ['excel'], multiple: true, engine: 'office' },
  { id: 'pptToPdf', category: 'convertTo', icon: 'presentation', slugs: { ca: 'powerpoint-a-pdf', es: 'powerpoint-a-pdf', gl: 'powerpoint-a-pdf' }, accept: ['powerpoint'], multiple: true, engine: 'office' },
  // Convert from PDF
  { id: 'pdfToImages', category: 'convertFrom', icon: 'file-image', slugs: { ca: 'pdf-a-jpg', es: 'pdf-a-jpg', gl: 'pdf-a-jpg' }, accept: ['pdf'], multiple: false, popular: true },
  { id: 'pdfToText', category: 'convertFrom', icon: 'type', slugs: { ca: 'pdf-a-text', es: 'pdf-a-texto', gl: 'pdf-a-texto' }, accept: ['pdf'], multiple: false },
  { id: 'pdfToWord', category: 'convertFrom', icon: 'file-type', slugs: { ca: 'pdf-a-word', es: 'pdf-a-word', gl: 'pdf-a-word' }, accept: ['pdf'], multiple: false, popular: true },
  { id: 'pdfa', category: 'convertFrom', icon: 'archive', slugs: { ca: 'pdf-a-pdfa', es: 'pdf-a-pdfa', gl: 'pdf-a-pdfa' }, accept: ['pdf'], multiple: true, engine: 'gs' },
  // Edit
  { id: 'sign', category: 'edit', icon: 'signature', slugs: { ca: 'signar-pdf', es: 'firmar-pdf', gl: 'asinar-pdf' }, accept: ['pdf'], multiple: false, popular: true },
  { id: 'pageNumbers', category: 'edit', icon: 'list-ordered', slugs: { ca: 'numerar-pagines', es: 'numerar-paginas', gl: 'numerar-paxinas' }, accept: ['pdf'], multiple: false },
  { id: 'watermark', category: 'edit', icon: 'stamp', slugs: { ca: 'marca-aigua', es: 'marca-de-agua', gl: 'marca-de-auga' }, accept: ['pdf'], multiple: false },
  { id: 'flatten', category: 'edit', icon: 'layers', slugs: { ca: 'aplanar-pdf', es: 'aplanar-pdf', gl: 'aplanar-pdf' }, accept: ['pdf'], multiple: true },
  // Security & privacy
  { id: 'redact', category: 'security', icon: 'eraser', slugs: { ca: 'censurar-pdf', es: 'censurar-pdf', gl: 'censurar-pdf' }, accept: ['pdf'], multiple: false, engine: 'mupdf' },
  { id: 'metadata', category: 'security', icon: 'tags', slugs: { ca: 'metadades-pdf', es: 'metadatos-pdf', gl: 'metadatos-pdf' }, accept: ['pdf'], multiple: false },
  { id: 'protect', category: 'security', icon: 'lock', slugs: { ca: 'protegir-pdf', es: 'proteger-pdf', gl: 'protexer-pdf' }, accept: ['pdf'], multiple: false },
  { id: 'unlock', category: 'security', icon: 'lock-open', slugs: { ca: 'desbloquejar-pdf', es: 'desbloquear-pdf', gl: 'desbloquear-pdf' }, accept: ['pdf'], multiple: false },
];

export type PageId = 'privacy' | 'licenses';

export const PAGE_SLUGS: Record<PageId, Record<Lang, string>> = {
  privacy: { ca: 'privacitat', es: 'privacidad', gl: 'privacidade' },
  licenses: { ca: 'llicencies', es: 'licencias', gl: 'licenzas' },
};

export function getTool(id: ToolId): ToolMeta {
  const tool = TOOLS.find((t) => t.id === id);
  if (!tool) throw new Error(`Unknown tool ${id}`);
  return tool;
}

/** Tools that are present in this build (office tools can be disabled at build time). */
export function enabledTools(officeEnabled: boolean): ToolMeta[] {
  return TOOLS.filter((t) => officeEnabled || t.engine !== 'office');
}

export function toolPath(lang: Lang, id: ToolId): string {
  return `/${lang}/${getTool(id).slugs[lang]}/`;
}

export function pagePath(lang: Lang, id: PageId): string {
  return `/${lang}/${PAGE_SLUGS[id][lang]}/`;
}

export function homePath(lang: Lang): string {
  return `/${lang}/`;
}

export type Route =
  | { kind: 'root' }
  | { kind: 'home'; lang: Lang }
  | { kind: 'tool'; lang: Lang; tool: ToolId }
  | { kind: 'page'; lang: Lang; page: PageId }
  | { kind: 'notFound'; lang: Lang };

export function parsePath(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return { kind: 'root' };
  const [first, second] = parts;
  if (!isLang(first)) return { kind: 'notFound', lang: DEFAULT_LANG };
  const lang = first;
  if (parts.length === 1) return { kind: 'home', lang };
  if (parts.length === 2) {
    const tool = TOOLS.find((t) => t.slugs[lang] === second);
    if (tool) return { kind: 'tool', lang, tool: tool.id };
    const page = (Object.keys(PAGE_SLUGS) as PageId[]).find((p) => PAGE_SLUGS[p][lang] === second);
    if (page) return { kind: 'page', lang, page };
  }
  return { kind: 'notFound', lang };
}

export function routePath(route: Route): string {
  switch (route.kind) {
    case 'root':
      return '/';
    case 'home':
      return homePath(route.lang);
    case 'tool':
      return toolPath(route.lang, route.tool);
    case 'page':
      return pagePath(route.lang, route.page);
    case 'notFound':
      return homePath(route.lang);
  }
}

/** Same route in another language. */
export function translateRoute(route: Route, lang: Lang): Route {
  switch (route.kind) {
    case 'root':
    case 'home':
    case 'notFound':
      return { kind: 'home', lang };
    case 'tool':
      return { ...route, lang };
    case 'page':
      return { ...route, lang };
  }
}
